import React from 'react';
import styled from 'styled-components';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { Button } from '@avangard/ui/core';

import { useEnqueueStacks } from '@modules/layout/hooks';
import { ExtendedFormik, Form } from '@modules/layout/organisms';
import { FormStyled } from '@modules/layout/styled';
import { UpdateStudentRequest } from '@modules/students/requests';
import { DialogStudentDelete } from '@modules/students/moleculas';
import { UpdateStudentMutation, RemoveStudentMutation } from '@modules/students/graphql';
import { StudentSummaryEditFormGroup } from './student-summary-edit-form-group';
import { StudentEducationEditFormGroup } from './student-education-edit-form-group';
import { StudentAccessFormGroup } from './student-access-form-group';

import type { StudentEntity } from '@modules/students/entities';
import type { UpdateStudentFormValues } from '@modules/students/requests';
import type {
    UpdateStudentMutationType,
    UpdateStudentMutationVariables,
    RemoveStudentMutationType,
    RemoveStudentMutationVariables,
} from '@modules/types/graphql';

export enum StudentEditFormTypesEnum {
    summary = 'summary',
    education = 'education',
    access = 'access',
}

type StudentEditFormProps = {
    type: StudentEditFormTypesEnum;
    student: StudentEntity;
    canEdit: boolean;
    canViewAccess: boolean;
    canCreateAccess: boolean;
    canSendAccess: boolean;
    canDeleteAccess: boolean;
    canDeleteStudent: boolean;
    onDeleteStudent: () => void;
};

const StyledForm = styled(Form)`
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    width: 100%;
`;

const StudentEditForm = (props: StudentEditFormProps): React.ReactElement => {
    const {
        type,
        student,
        canEdit,
        canViewAccess,
        canCreateAccess,
        canDeleteAccess,
        canSendAccess,
        canDeleteStudent,
        onDeleteStudent,
    } = props;

    const { t } = useTranslation(['common', 'errors', 'students']);

    const { enqueueSuccess, enqueueError } = useEnqueueStacks();

    const [updateStudent] = useMutation<UpdateStudentMutationType, UpdateStudentMutationVariables>(
        UpdateStudentMutation,
    );

    const [removeStudent, { loading: removeStudentLoading }] = useMutation<
        RemoveStudentMutationType,
        RemoveStudentMutationVariables
    >(RemoveStudentMutation);

    const handleClickRemoveStudent = async (): Promise<void> => {
        try {
            const { data: removeStudentData } = await removeStudent({
                variables: { id: student.id },
            });

            if (removeStudentData?.removeStudent) {
                enqueueSuccess(t('students:notifiers.delete_student.success'));
                onDeleteStudent();
            } else {
                enqueueError(t('students:notifiers.delete_student.error'));
            }
        } catch (e: any) {
            enqueueError(e);
        }
    };

    let initialValues: UpdateStudentFormValues = {
        summary: {
            fullName: student.fullName,
            gender: student.gender,
            birthdate: student.birthdate,
            region: student.region.exists() ? student.region : null,
            location: student.location.exists() ? student.location : null,
            address: student.address,
            phone: student.phone,
            email: student.email,
            vk: student.socials.vk,
        },
        education: {
            schoolRegionId: student.school.region.id,
            schoolLocationId: student.school.location.id,
            schoolId: student.school.id,
            schoolClassProfile: student.schoolClassProfile,
            performanceManagement: student.preparePerfomanceManagmentForForm(),
            creditOfPoints: student.prepareCreditOfPointForForm(),
            interest: student.prepareInterestForForm(),
            facultative: student.facultative,
            clubs: student.clubs,
            sportSection: student.sportSection,
            sportsCategory: student.sportsCategory,
        },
    };

    const renderedFormComponent = React.useMemo(() => {
        switch (type) {
            case StudentEditFormTypesEnum.summary:
                return <StudentSummaryEditFormGroup student={student} />;

            case StudentEditFormTypesEnum.education:
                return <StudentEducationEditFormGroup student={student} />;

            case StudentEditFormTypesEnum.access:
                return (
                    <StudentAccessFormGroup
                        student={student}
                        canViewAccess={canViewAccess}
                        canCreateAccess={canCreateAccess}
                        canSendAccess={canSendAccess}
                        canDeleteAccess={canDeleteAccess}
                    />
                );

            default:
                return null;
        }
    }, [student, type, canViewAccess, canCreateAccess, canDeleteAccess, canSendAccess]);

    return (
        <ExtendedFormik
            enableReinitialize
            validateOnBlur={false}
            validateOnChange={false}
            validationSchema={yup.object({
                education: yup.object({
                    schoolRegionId: yup.string().required(t('errors:validations.required') ?? ''),
                    schoolLocationId: yup.string().required(t('errors:validations.required') ?? ''),
                    schoolId: yup.string().required(t('errors:validations.required') ?? ''),
                }),
                summary: yup.object({
                    fullName: yup.string().required(t('errors:validations.required') ?? ''),
                    email: yup.string().required(t('errors:validations.required') ?? ''),
                    birthdate: yup.string().required(t('errors:validations.required') ?? ''),
                    gender: yup.string().required(t('errors:validations.required') ?? ''),
                }),
            })}
            initialValues={initialValues}
            onSubmit={async values => {
                try {
                    const item = new UpdateStudentRequest(values);

                    const { data: updateStudentData } = await updateStudent({
                        variables: { id: student.id, item },
                    });

                    if (updateStudentData?.updateStudent?.id) {
                        enqueueSuccess(t('students:notifiers.edit_student.success'));
                    } else {
                        enqueueError(t('students:notifiers.edit_student.error'));
                    }
                } catch (e) {
                    console.log(e);
                    throw e;
                }
            }}
        >
            {formikProps => {
                const { isSubmitting } = formikProps;

                const isDisabledSubmit = isSubmitting || removeStudentLoading;

                return (
                    <StyledForm>
                        <FormStyled.FormColumnList>
                            {renderedFormComponent}

                            {type === StudentEditFormTypesEnum.access ? null : (
                                <FormStyled.FormColumn>
                                    <FormStyled.FormRowSubmit>
                                        <Button
                                            type='submit'
                                            loading={isDisabledSubmit}
                                            disabled={!canEdit}
                                        >
                                            {t('common:actions.save')}
                                        </Button>

                                        {canDeleteStudent && canEdit ? (
                                            <DialogStudentDelete
                                                loading={isDisabledSubmit}
                                                onAccept={handleClickRemoveStudent}
                                            />
                                        ) : null}
                                    </FormStyled.FormRowSubmit>
                                </FormStyled.FormColumn>
                            )}
                        </FormStyled.FormColumnList>
                    </StyledForm>
                );
            }}
        </ExtendedFormik>
    );
};

export { StudentEditForm };
